import { Navigate, useLocation } from 'react-router-dom'
import { authStore } from '@/store/auth'
import { useIdentityQuery } from '@/features/preferences'

interface GuestRouteProps {
  children: React.ReactNode
}

/**
 * Mirror of ProtectedRoute for auth-only pages (login, register, etc.).
 * If the user is already authenticated, bounce them onward instead of
 * rendering the bare auth page (which would otherwise show with the app
 * navbar painted on top).
 *
 * An explicit `?next=` always wins and is trusted as-is — RequireOrg is the
 * only authority on whether the destination is actually reachable (plan 024
 * design decision 7: "?next= is a hint"). This also covers the stale-bookmark
 * case (an org-less user signing in with next=/app) with no extra code here:
 * RequireOrg bounces them from wherever `next` pointed.
 *
 * With no `next`, this runs the same org/onboarding ladder RequireOrg and the
 * post-verification redirect use, so an org-less authenticated user visiting
 * /login or /register directly no longer gets dumped on /dashboard (where
 * every real request 403s) — the bug this rewrite exists to fix.
 */
export default function GuestRoute({ children }: GuestRouteProps) {
  const { user } = authStore()
  const location = useLocation()
  const next = new URLSearchParams(location.search).get('next')

  // Skip the identity fetch entirely while unauthenticated — an unauthenticated
  // /api/me would 401 and drive the shared http client's doomed refresh-retry.
  const { data, isLoading } = useIdentityQuery({ enabled: !!user && !next })

  if (!user) {
    return <>{children}</>
  }

  if (next) {
    return <Navigate to={next} replace />
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    )
  }

  if (!data || data.orgs.length === 0) {
    return <Navigate to="/setup-organization" replace />
  }
  // Not the same check as the org gate above — see the "RequireOrg/GuestRoute" paragraph in
  // docs/adr/0009-provisioning-after-verification.md. That gate (orgs.length === 0) is
  // security-adjacent and must never be replaced by this one; this rung is UX-only, deciding
  // wizard vs. /app for a user who already has an org.
  if (!user.onboarding_complete) {
    return <Navigate to="/welcome" replace />
  }
  return <Navigate to="/app" replace />
}
